import { Fragment, ReactNode, useEffect } from 'react'
import { Answers, FieldDef, Recipe, RowEntry, SharedKey, TemplateDefinition, TemplateItem } from './types'
import { TEMPLATES } from './templates'
import { Button, FormGroup, SelectField, TextAreaField } from '../components/common'
import styles from './InterviewTemplate.module.scss'

export type InterviewTemplateProps = {
  definition: TemplateDefinition
  answers: Answers
  disabled: boolean
  onChange?: (key: string, value: string) => void
  onRecipesChange?: (recipes: Recipe[]) => void
  recipeFieldLabels: Record<string, string>
  recipeTypeLabels: Record<string, string>
  recipeDefaultOption: string
  onDownloadRecipes?: (recipeIndex: number | 'all') => void
  recipesSoftDisabled?: boolean
}

function DisabledValue({ label, value }: { label: string; value: unknown }) {
  const present = value !== null && value !== undefined && String(value).length > 0
  return (
    <div className="form-group-hover form-group label-floating">
      <label className="control-label">{label}</label>
      <p className={`form-control ${styles.readOnlyValue}`} style={{ wordBreak: 'break-all', height: 'auto', whiteSpace: 'pre-wrap' }}>
        {present ? String(value) : 'N/A'}
      </p>
    </div>
  )
}

function Field({ field, props }: { field: FieldDef; props: InterviewTemplateProps }) {
  const value = props.answers[field.key]
  const body = props.disabled ? (
    <DisabledValue label={field.label} value={value} />
  ) : (
    <FormGroup className={`form-group ${field.kind === 'text' ? 'label-static' : 'label-floating'}`}>
      <label className="control-label" htmlFor={field.key}>
        {field.label}
      </label>
      {field.help !== undefined && field.help !== '' && <i className="control-label help-label">{field.help}</i>}
      {field.kind === 'text' ? (
        <textarea
          id={field.key}
          className="form-control"
          rows={Math.max(4, field.rows ?? 0)}
          value={String(value ?? '')}
          onChange={(event) => props.onChange?.(field.key, event.target.value)}
        />
      ) : (
        <input
          id={field.key}
          type={field.kind === 'string' ? 'text' : 'number'}
          step={field.kind === 'number' ? 'any' : undefined}
          className="form-control"
          readOnly={field.readonly}
          style={field.readonly ? { backgroundColor: '#f4f4f4' } : undefined}
          value={String(value ?? '')}
          onChange={(event) => props.onChange?.(field.key, event.target.value)}
        />
      )}
    </FormGroup>
  )
  return (
    <div className={field.col}>
      {field.heading && <h4 className="text-center">{field.heading}</h4>}
      <div className="col-xs-12">{body}</div>
    </div>
  )
}

function computeImc(answers: Answers): string {
  const weight = parseFloat(String(answers.weight ?? '').replace(/,/g, '.'))
  const height = parseFloat(String(answers.height ?? '').replace(/,/g, '.')) / 100.0
  const imc = weight / (height * height)
  if (Number.isNaN(imc)) {
    return ''
  }
  return imc.toFixed(2)
}

function ImcFields({ props }: { props: InterviewTemplateProps }) {
  const computed = props.disabled ? '' : computeImc(props.answers)
  // Main rewrites the field on every weight/height keystroke, blank included, and
  // only leaves a stored value alone while neither measure has been entered.
  const hasMeasures = String(props.answers.weight ?? '') !== '' || String(props.answers.height ?? '') !== ''
  const shown = props.disabled || !hasMeasures ? props.answers.imc : computed
  const withImc: InterviewTemplateProps = {
    ...props,
    answers: { ...props.answers, imc: shown },
  }

  // The field is readonly rather than derived, so the value has to reach the
  // submitted answers the same way a typed one does.
  const { disabled, onChange, answers } = props
  useEffect(() => {
    if (disabled || !hasMeasures || computed === answers.imc) {
      return
    }
    onChange?.('imc', computed)
  }, [computed, disabled, hasMeasures, answers.imc, onChange])

  return (
    <>
      <Field field={{ key: 'weight', kind: 'number', label: 'Peso (kg)', col: 'col-md-3 col-xs-12' }} props={props} />
      <Field field={{ key: 'height', kind: 'number', label: 'Estatura (cm)', col: 'col-md-3 col-xs-12' }} props={props} />
      <Field field={{ key: 'imc', kind: 'string', label: 'IMC', col: 'col-md-3 col-xs-12', readonly: true }} props={withImc} />
    </>
  )
}

function RecipeFields({ props }: { props: InterviewTemplateProps }) {
  const recipes = props.answers.recipes ?? []
  const labels = props.recipeFieldLabels
  const getTypeLabel = (id: string) => labels[id] ?? props.recipeTypeLabels[id] ?? id
  const offeredOptions = Object.entries(labels).map(([value, label]) => ({ value, label }))
  const getTypeOptions = (id: string) => (id in labels ? offeredOptions : [...offeredOptions, { value: id, label: getTypeLabel(id) }])

  if (recipes.length === 0 && props.disabled) {
    return (
      <div className="hc-printables">
        <p>No hay recetas para mostrar</p>
      </div>
    )
  }

  const updateRecipe = (index: number, patch: Partial<Recipe>) => {
    props.onRecipesChange?.(recipes.map((recipe, i) => (i === index ? { ...recipe, ...patch } : recipe)))
  }

  if (!props.disabled) {
    return (
      <div className={styles.recipes}>
        {recipes.map((recipe, index) => (
          <div className={styles.recipe} key={index}>
            <div className={styles.recipeHeader}>
              <SelectField
                labelStyle="plain"
                className={styles.recipeSelect}
                aria-label="Tipo de receta"
                value={recipe.id}
                onChange={(id) => updateRecipe(index, { id })}
                options={getTypeOptions(recipe.id)}
              />
              <Button
                variant="none"
                iconAction="delete"
                title="Eliminar"
                aria-label="Eliminar receta"
                onClick={() => props.onRecipesChange?.(recipes.filter((_, i) => i !== index))}
              >
                <i className="material-icons" aria-hidden="true">
                  delete
                </i>
              </Button>
            </div>
            <TextAreaField
              labelStyle="plain"
              className={styles.recipeText}
              aria-label="Detalle de la receta"
              placeholder="Escribe aquí la receta o indicación"
              rows={Math.max(4, recipe.value.split('\n').reduce((total, line) => total + Math.trunc(line.length / 68) + 1, 0))}
              value={recipe.value}
              onChange={(value) => updateRecipe(index, { value })}
            />
          </div>
        ))}
        <Button
          variant="none"
          id="add-new-recipe-button"
          className={styles.addRecipe}
          onClick={() => props.onRecipesChange?.([...recipes, { id: props.recipeDefaultOption, value: '' }])}
        >
          <i className="material-icons" aria-hidden="true">
            add
          </i>
          Agregar receta
        </Button>
      </div>
    )
  }

  return (
    <div className="hc-printables">
      <Button
        className="print-button"
        softDisabled={props.recipesSoftDisabled}
        data-testid="download-all-recipes"
        onClick={() => props.onDownloadRecipes?.('all')}
      >
        <i className="material-icons">cloud_download</i> Descargar todas
      </Button>
      <table className="table recipes-table">
        <colgroup>
          <col span={1} style={{ width: '30%' }} />
          <col span={1} style={{ width: '70%' }} />
        </colgroup>
        <tbody>
          {recipes.map((recipe, index) => (
            <tr className="form-group" key={index}>
              <td>
                <div className="label-static">
                  <Button
                    className="print-button print-one"
                    softDisabled={props.recipesSoftDisabled}
                    data-testid="download-recipe"
                    onClick={() => props.onDownloadRecipes?.(index)}
                  >
                    <i className="material-icons">cloud_download</i>
                  </Button>
                  <label className="control-label">{getTypeLabel(recipe.id)}</label>
                </div>
              </td>
              <td>
                <p className={`form-control ${styles.readOnlyValue}`} style={{ wordWrap: 'break-word', textAlign: 'justify', height: 'auto', whiteSpace: 'pre-wrap' }}>
                  {recipe.value && recipe.value.length > 0 ? recipe.value : 'N/A'}
                </p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Shared({ shared, props }: { shared: SharedKey; props: InterviewTemplateProps }) {
  switch (shared) {
    case 'basic_measure_fields':
      return (
        <div className="row">
          <Field field={{ key: 'weight', kind: 'string', label: 'Peso', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'height', kind: 'string', label: 'Estatura', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'pressure', kind: 'string', label: 'Presión', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'pulse', kind: 'string', label: 'Pulso', col: 'col-md-3 col-xs-12' }} props={props} />
        </div>
      )
    case 'basic_measure_fields_v2':
      return (
        <div className="row">
          <ImcFields props={props} />
          <Field field={{ key: 'arterial-pressure', kind: 'string', label: 'Presión (mmHg)', col: 'col-md-3 col-xs-12', help: '' }} props={props} />
          <Field field={{ key: 'pulse', kind: 'number', label: 'Pulso (ppm)', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'oxygen-saturation', kind: 'number', label: 'SaO2 (%)', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'capillary-blood-glucose', kind: 'number', label: 'HGT (mg/dl)', col: 'col-md-3 col-xs-12' }} props={props} />
        </div>
      )
    case 'imc_fields':
      return <ImcFields props={props} />
    case 'weight_indicators_fields':
      return (
        <>
          <Field field={{ key: 'body_fat', kind: 'number', label: 'Grasa Corporal (%)', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'body_mass', kind: 'number', label: 'Masa Muscular (%)', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'body_water', kind: 'number', label: 'Agua Corporal (%)', col: 'col-md-3 col-xs-12' }} props={props} />
          <Field field={{ key: 'bone_mass', kind: 'number', label: 'Masa Ósea', col: 'col-md-3 col-xs-12' }} props={props} />
        </>
      )
    case 'physical_analysis_fields':
      return (
        <>
          <div className="row">
            <Field field={{ key: 'face', kind: 'string', label: 'Cara', col: 'col-md-6 col-xs-12' }} props={props} />
            <Field field={{ key: 'mouth', kind: 'string', label: 'Boca', col: 'col-md-6 col-xs-12' }} props={props} />
          </div>
          <div className="row">
            <Field field={{ key: 'pharynx', kind: 'string', label: 'Faringe', col: 'col-md-6 col-xs-12' }} props={props} />
            <Field field={{ key: 'neck', kind: 'string', label: 'Cuello', col: 'col-md-6 col-xs-12' }} props={props} />
          </div>
          <div className="row">
            <Field field={{ key: 'chest', kind: 'string', label: 'Tórax', col: 'col-md-6 col-xs-12' }} props={props} />
            <Field field={{ key: 'abdomen', kind: 'string', label: 'Abdomen', col: 'col-md-6 col-xs-12' }} props={props} />
          </div>
          <div className="row">
            <Field field={{ key: 'extremities', kind: 'string', label: 'Extremidades', col: 'col-md-6 col-xs-12' }} props={props} />
          </div>
        </>
      )
    case 'recipe_fields':
      return <RecipeFields props={props} />
    case 'notes_section':
      return null
  }
}

function RowEntries({ entries, props }: { entries: RowEntry[]; props: InterviewTemplateProps }) {
  return (
    <>
      {entries.map((entry, index) => (
        <Fragment key={index}>{'shared' in entry ? <Shared shared={entry.shared} props={props} /> : <Field field={entry} props={props} />}</Fragment>
      ))}
    </>
  )
}

function Item({ item, props }: { item: TemplateItem; props: InterviewTemplateProps }): ReactNode {
  if ('h3' in item) {
    return <h3 className="text-center">{item.h3}</h3>
  }
  if ('h4' in item) {
    return <h4 className="text-center">{item.h4}</h4>
  }
  if ('row' in item) {
    return (
      <div className="row">
        <RowEntries entries={item.row} props={props} />
      </div>
    )
  }
  if ('cols' in item) {
    return <RowEntries entries={item.cols} props={props} />
  }
  if ('shared' in item) {
    return <Shared shared={item.shared} props={props} />
  }
  const included = TEMPLATES[item.include]
  if (!included) {
    return null
  }
  return <InterviewTemplateView {...props} definition={included} />
}

export function InterviewTemplateView(props: InterviewTemplateProps) {
  return (
    <>
      {props.definition.map((item, index) => (
        <Fragment key={index}>
          <Item item={item} props={props} />
        </Fragment>
      ))}
    </>
  )
}
